/**
 * A carrier purchase line as the planning board sends it for a single order.
 *
 * The send dialog is the source of truth: the lines built here replace every
 * `OrderLinePurchase` on the order, so a line that is not in this list is a
 * line that gets removed.
 */
export interface PlanningCostLine {
  productTypeId: string;
  /** Quantity * 100 (100 = one unit). */
  amount: number;
  /** Unit price in cents. */
  unitPrice: number;
  externalNote?: string;
}

/** Seeded / production product type for Viapass / kilometerheffing. */
export const KILOMETER_CHARGE_PRODUCT_TYPE_ID = 'KMH';

export interface BuildOrderCostLinesInput {
  /** Product type of the transport line, absent when general settings has none. */
  transportProductTypeId: string | null | undefined;
  /** Transport price in cents. */
  transportCents: number;
  /** Product type of the diesel surcharge line, absent when not configured. */
  dieselProductTypeId: string | null | undefined;
  /** This order's share of the diesel surcharge, in cents. */
  dieselCents: number;
  /** Product type of the kilometer charge line, absent when not configured. */
  kilometerChargeProductTypeId?: string | null;
  /** Kilometerheffing total for this order, in cents. */
  kilometerChargeCents?: number;
  /** Any other cost the planner added by hand. */
  costLines: PlanningCostLine[];
}

/**
 * Full purchase-line set for one order: the transport line, the diesel
 * surcharge line, and every other cost the planner added. Lines that would cost
 * nothing are left out so an empty input yields no purchase lines at all.
 *
 * Duplicate product types are kept as separate lines — an order can legitimately
 * carry two rows of the same cost type, and collapsing them would rewrite data
 * the planner did not touch.
 */
export function buildOrderCostLines({
  transportProductTypeId,
  transportCents,
  dieselProductTypeId,
  dieselCents,
  kilometerChargeProductTypeId,
  kilometerChargeCents,
  costLines,
}: BuildOrderCostLinesInput): PlanningCostLine[] {
  const lines: PlanningCostLine[] = [];

  if (transportProductTypeId && transportCents > 0) {
    lines.push({ productTypeId: transportProductTypeId, amount: 100, unitPrice: transportCents });
  }

  if (dieselProductTypeId && dieselCents > 0) {
    lines.push({ productTypeId: dieselProductTypeId, amount: 100, unitPrice: dieselCents });
  }

  if (kilometerChargeProductTypeId && (kilometerChargeCents ?? 0) > 0) {
    lines.push({
      productTypeId: kilometerChargeProductTypeId,
      amount: 100,
      unitPrice: kilometerChargeCents ?? 0,
    });
  }

  for (const line of costLines) {
    if (line.amount > 0 && line.unitPrice > 0) {
      lines.push(line);
    }
  }

  return lines;
}

/** Total of the given lines in cents. */
export function sumCostLines(lines: PlanningCostLine[]): number {
  return lines.reduce((total, line) => total + (line.amount / 100) * line.unitPrice, 0);
}
